import React from 'react';
import { Bot, BotMessageSquare, User } from 'lucide-react';
import { useTranslation } from 'react-i18next';

type AIMode = 'auto' | 'suggestions' | 'manual';

interface ConversationAIModeIndicatorProps {
  mode: AIMode;
  hasNewMessage?: boolean;
  size?: 'sm' | 'md';
}

export function ConversationAIModeIndicator({
  mode,
  hasNewMessage = false,
  size = 'sm'
}: ConversationAIModeIndicatorProps) {
  const { t } = useTranslation('conversations');

  const modeConfig = {
    auto: {
      icon: Bot,
      color: 'text-green-600',
      bgColor: 'bg-green-100',
      label: t('modes.autoLabel')
    },
    suggestions: {
      icon: BotMessageSquare,
      color: 'text-blue-600',
      bgColor: 'bg-blue-100',
      label: t('modes.assistLabel')
    },
    manual: {
      icon: User,
      color: 'text-gray-600',
      bgColor: 'bg-gray-100',
      label: t('modes.manualLabel')
    }
  };

  const config = modeConfig[mode] || modeConfig.manual; // Fallback to manual if mode is invalid
  const IconComponent = config.icon;
  
  const sizeClass = size === 'sm' ? 'h-3 w-3' : 'h-4 w-4';
  const badgeSize = size === 'sm' ? 'px-1.5 py-0.5' : 'px-2 py-1';
  const textSize = size === 'sm' ? 'text-xs' : 'text-sm';

  // Pulse animation for attention-needed conversations
  const shouldPulse = hasNewMessage && (mode === 'manual' || mode === 'suggestions');

  return (
    <div className={`
      inline-flex items-center gap-1 rounded-full ${badgeSize} ${config.bgColor}
      ${shouldPulse ? 'animate-pulse' : ''}
    `}>
      <IconComponent className={`${sizeClass} ${config.color}`} />
      <span className={`${textSize} font-medium ${config.color}`}>
        {config.label}
      </span>
    </div>
  );
}